import type * as Highcharts from 'highcharts';
import type { NamedSolution } from '../../types';
import { compare_name_asc } from '../utils';

// Shared axis config so the "Cleaning Schedules" heatmap and the stacked "Soiling Ratio"
// chart use the exact same calendar: identical plot margins (columns aligned pixel-for-pixel),
// identical datetime tick interval, and a real time axis (same min/max => same ticks).
const DAY_MS = 24 * 3600 * 1000;
const NEAREST_DAY_MAX_MS = 7 * DAY_MS;
export const ALIGNED_CHART_MARGIN_LEFT = 120;
export const ALIGNED_CHART_MARGIN_RIGHT = 40;
export const ALIGNED_CHART_TICK_INTERVAL = 30 * DAY_MS;

type Cell = [number, number, number | null];

const pad2 = (value: number) => ('0' + value).slice(-2);

/** Index of the date in the categories, else of the closest category less than a week away, else -1. */
const categoryIndex = (categories: string[], date: string): number => {
  const exact = categories.findIndex((catDate) => catDate === date);
  if (exact !== -1) return exact;
  const targetTime = new Date(Date.parse(date)).getTime();
  let closestIndex = -1;
  let minDiff = Infinity;
  categories.forEach((catDate, catIndex) => {
    const diff = Math.abs(new Date(catDate).getTime() - targetTime);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = catIndex;
    }
  });
  return minDiff < NEAREST_DAY_MAX_MS ? closestIndex : -1;
};

const cleaningCells = (item: NamedSolution, y: number, categories: string[]): Cell[] => {
  if (item?.cleaning_graph && Array.isArray(item.cleaning_graph)) {
    return item.cleaning_graph.flatMap((value, index): Cell[] => (value !== 0 ? [[index, y, value]] : []));
  }
  const daysToClean = Math.max(parseInt(String(item.time_to_clean)), 1);
  const cells: Cell[] = [];
  item?.schedule?.forEach((date, index) => {
    const startIndex = categoryIndex(categories, date);
    if (startIndex === -1) return;
    const cleaningValue = index % 2 === 0 ? 1 : 0.5;
    for (let i = 0; i < daysToClean; i++) cells.push([startIndex + i, y, cleaningValue]);
  });
  return cells;
};

const scheduleSeries = (sortedData: NamedSolution[], rainDates: string[] | undefined, categories: string[]) => {
  const cleaning: Cell[] = [];
  const rain: Cell[] = [];
  sortedData.forEach((item, y) => {
    cleaning.push(...cleaningCells(item, y, categories));
    rainDates?.forEach((date) => {
      const rainIndex = categoryIndex(categories, date);
      if (rainIndex !== -1) rain.push([rainIndex, y, 5]);
    });
    // Empty cells at both ends keep the full calendar on the axis.
    if (rain.findIndex((d) => d[0] === 0) === -1) rain.push([0, 0, null]);
    if (rain.findIndex((d) => d[0] === categories.length - 1) === -1) rain.push([categories.length - 1, 0, null]);
  });
  // Category indices become timestamps so the heatmap shares a datetime axis with the soiling ratio chart.
  const toMs = (idx: number) => Date.parse(categories[Math.min(Math.max(idx, 0), categories.length - 1)]);
  const toTimestamps = (rows: Cell[]) => rows.map(([x, y, v]): Cell => [toMs(x), y, v]);
  return [
    { type: 'heatmap', name: 'Cleaning', data: toTimestamps(cleaning), color: 'orange' },
    { type: 'heatmap', name: 'Rain', data: toTimestamps(rain), color: 'blue' },
  ] as Highcharts.SeriesHeatmapOptions[];
};

const scheduleTooltip = (count: number): Highcharts.TooltipFormatterCallbackFunction =>
  function () {
    const point = this.point;
    // On a datetime axis point.x is a timestamp.
    const d = new Date(point.x);
    const dateFormat =
      count > 1 ? d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()) : d.getDate() + '/' + pad2(d.getMonth() + 1);
    const value = (point as Highcharts.Point & { value?: number }).value;
    const eventType = value === 3 ? 'Blacklist' : value === 4 ? 'Special Cleaning' : this.series.name;
    const yCategories = (this.series.yAxis as Highcharts.Axis & { categories: string[] }).categories;
    return yCategories[point.y || 0] + '<br>' + eventType + ' on ' + dateFormat;
  };

/** Cleaning and rain days of each solution, one row per solution; `null` without daily times. */
export const buildCleaningSchedulesOptions = (
  data: NamedSolution[] | undefined,
  rainDates: string[] | undefined,
): Highcharts.Options | null => {
  const categories = data?.[0]?.data?.daily?.times || [];
  if (!data || !categories.length) return null;
  const sortedData = [...data].sort(compare_name_asc);

  return {
    chart: {
      type: 'heatmap',
      height: 170 + 25 * data.length,
      marginTop: 50,
      marginBottom: 120,
      marginLeft: ALIGNED_CHART_MARGIN_LEFT,
      marginRight: ALIGNED_CHART_MARGIN_RIGHT,
      zooming: { type: 'x' },
    },
    // Same local-time calendar as the soiling ratio chart so ticks line up
    time: { useUTC: false },
    title: { text: 'Cleaning Schedules', align: 'left' },
    boost: { useGPUTranslations: true, usePreallocated: true },
    legend: { enabled: true, itemMarginBottom: -20 },
    xAxis: {
      type: 'datetime',
      title: { text: 'Date' },
      min: Date.parse(categories[0]),
      max: Date.parse(categories[categories.length - 1]),
      tickInterval: ALIGNED_CHART_TICK_INTERVAL,
      labels: {
        formatter: function () {
          const d = new Date(this.value);
          return pad2(d.getDate()) + '/' + pad2(d.getMonth() + 1);
        },
      },
      gridLineWidth: 1,
    },
    yAxis: { categories: sortedData.map((item) => item.name), title: { text: '' } },
    tooltip: { formatter: scheduleTooltip(data.length) },
    colorAxis: {
      min: 0,
      max: 5,
      stops: [
        [0, '#FFFFFF'],
        [0.2, '#FF6B35'],
        [0.4, '#C55824'],
        [0.6, '#808080'],
        [0.8, '#b75cf8ff'],
        [1, '#0066CC'],
      ],
      showInLegend: false,
    },
    credits: { enabled: false },
    plotOptions: {
      series: { turboThreshold: 10000, animation: { duration: 500 } },
      heatmap: { enableMouseTracking: true, colsize: DAY_MS },
    },
    series: scheduleSeries(sortedData, rainDates, categories),
  };
};
